<template>
  <div class="login">
    <div class="login-container">
      <div class="logo">
        <img src="/logo-white.svg"
             alt="Logo">
      </div>

      <form @submit.prevent>
        <div class="login-form"
             :class="loginFormClasses">
          <div class="login-form__header">
            {{ $t("login.signIn") }}
          </div>

          <div class="login-form__fields">
            <div class="login-form__field">
              <label class="login-form__field-label"
                     for="email">
                {{ $t("login.email") }}
              </label>
              <input class="login-form__field-input"
                     :class="{ 'login-form__field-input_invalid': loginError }"
                     type="text"
                     id="email"
                     v-model.trim="email"
                     v-autofocus
                     @input="clearErrors">
            </div>
            <div class="login-form__field">
              <label class="login-form__field-label"
                     for="password">
                {{ $t("login.password") }}
              </label>
              <input class="login-form__field-input"
                     :class="{ 'login-form__field-input_invalid': loginError }"
                     :type="showPassword ? 'text' : 'password'"
                     id="password"
                     v-model="password"
                     @input="clearErrors">

              <div class="login-form__field-password-icon"
                   :class="showPassword ? 'login-form__field-password-icon_on' : 'login-form__field-password-icon_off'"
                   :title="showPassword ? 'Hide password' : 'Show password'"
                   @click="showPassword = !showPassword">
                <component width="24"
                           height="24"
                           :is="showPassword ? 'icon-eye-on' : 'icon-eye-off'" />
              </div>
            </div>
          </div>

          <div class="login-form__error-message"
               v-show="loginError">
            {{ loginError }}
          </div>
        </div>

        <div class="submit-button-wrapper">
          <base-button class="submit-button"
                       color="secondary"
                       type="submit"
                       :class="submitBtnClasses"
                       :loading="loginInProgress"
                       @click="onSubmit">
            {{ $t("login.login") }}
          </base-button>
        </div>
      </form>

      <a class="forgot-password-link link"
        :href="addExtrasToUrl('https://www.netnanny.com/password-help/')"
        @click="forgotPassword">
        {{ $t("login.forgotPassword") }}
      </a>
    </div>

    <div class="footer"
         :class="footerClasses">
      <div class="terms">
        <a class="link"
          :href="addExtrasToUrl('https://www.netnanny.com/support/terms-conditions/')"
          target="_blank">
          {{ $t("login.termsOfService") }}
        </a>
        <span class="terms__divider">/</span>
        <a class="link"
          :href="addExtrasToUrl('https://www.netnanny.com/support/privacy-policy/')"
          target="_blank">
          {{ $t("login.privacyPolicy") }}
        </a>
      </div>

      <div v-if="$mq !== 'mobile'"
           class="app-info">
        {{ $t("login.buildNumber") }} {{ appVersion }} {{ appBuild }}
      </div>
    </div>
  </div>
</template>

<script>
  import { login } from '@/services/auth.js'
  import analytics from '@/services/analytics.js'

  export default {
    data() {
      return {
        email: '',
        password: '',
        loginInProgress: false,
        loginError: null,
        defaultErrorText: 'Invalid login or password',
        showPassword: false,
      }
    },

    computed: {
      ...mapGetters('app', {
        appVersion: 'getVersion',
        appBuild: 'getBuild'
      }),

      loginFormClasses() {
        return {
          'login-form-mobile': this.$mq === 'mobile'
        }
      },

      submitBtnClasses() {
        return {
          'submit-button-mobile': this.$mq === 'mobile'
        }
      },

      footerClasses() {
        return {
          'footer-mobile': this.$mq === 'mobile'
        }
      }
    },

    created() {
      analytics.sendEvent('SignIn_Sc')
    },

    methods: {

      addExtrasToUrl(p_url) {
        return p_url + this.$i18n.locale + "/?utm_source=parentApp&utm_medium=web";
      },

      onSubmit() {
        analytics.sendEvent('Pk_Login_SignIn')

        this.loginError = null
        this.loginInProgress = true

        login(this.email, this.password).then(({ authenticated }) => {
          if (authenticated) {
            if (this.$mq === 'mobile') {
              this.$router.push({ name: 'feed' })

              return
            }

            this.$router.push({ name: 'overview' })

            return
          }

          this.loginError = this.defaultErrorText
        }).catch(error => {
          if (error === '"email" must be a valid email') {
            this.loginError = 'Invalid e-mail format'

            return
          }

          this.loginError = this.defaultErrorText
        }).then(() => {
          this.loginInProgress = false
        })
      },

      forgotPassword() {
        analytics.sendEvent('Pk_Forgot_SignIn')
      },

      clearErrors() {
        this.loginError = null
      }
    }
  }
</script>

<style lang="scss" scoped>
  .login {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: map.get($colors, primary);
  }

  .login-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 300px;
    margin-bottom: 20px;

    img {
      width: 100%;
    }
  }

  .login-form {
    $side-padding-size: 24px;
    $error-color: #fb5e55;

    position: relative;
    width: 515px;
    margin-bottom: 48px;
    background-color: #fff;
    border-radius: 24px;
    box-shadow: 0 5px 9px 0 rgba(#000, .18);

    &__header {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 90px;
      font-weight: bold;
      font-size: 36px;
      line-height: 1.79;
      letter-spacing: -0.3px;
      color: #262450;
      border-bottom: 1px solid rgba(#000, .1);
    }

    &__field {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 82px;
      padding: 0 $side-padding-size;
      font-size: 20px;
      color: #232021;

      &:not(:last-child) {
        height: 79px;
        border-bottom: 1px solid rgba(#000, .1);
      }
    }

    &__field-label {
      padding-right: 1em;
    }

    &__field-label,
    &__field-input {
      line-height: 1.5;
    }

    &__field-input {
      flex-grow: 1;
      background-color: transparent;
      border: 0;
      text-align: right;
      color: inherit;

      &_invalid {
        color: $error-color;
        -webkit-text-fill-color: $error-color; // change autofilled input color in webkit browsers
      }
    }

    &__field-password-icon {
      display: flex;
      align-items: center;
      height: 100%;
      width: 40px + $side-padding-size;
      margin-right: -$side-padding-size; // make button bigger by "deleting" parent's padding-right
      padding-left: 16px;
      cursor: pointer;
      user-select: none;

      &_on {
        color: map.get($colors, primary);
      }

      &_off {
        color: #999;
      }
    }

    &__error-message {
      position: absolute;
      bottom: 0;
      left: 50%;
      padding: 5px 22px;
      font-size: 14px;
      color: #fff;
      background-color: $error-color;
      border-radius: 14px;
      transform: translate(-50%, 50%);
    }
  }

  .submit-button-wrapper {
    display: flex;
    justify-content: center;
  }

  .submit-button {
    width: 400px;
    margin: 0 auto 18px auto;
    padding: 23px;
    font-size: 28px;
    font-weight: bold;
  }

  .forgot-password-link {
    opacity: .6;
  }

  .footer {
    margin: 32px 0;
    color: #f2f2f2;
  }

  .terms {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 10px;

    &__divider {
      margin: 0 .5em;
      font-size: 18px;
      line-height: 1.5;
    }
  }

  .app-info {
    font-size: 14px;
    font-weight: 200;
    text-align: center;
    opacity: .6;
  }

  .login-form-mobile {
    width: 90%;
    margin: 0 auto;
    margin-bottom: 48px;

    .login-form__header {
      height: 65px;
    }

    .login-form__field {
      height: 60px;
    }

    .login-form__field-label {
      font-size: 14px;
      white-space: nowrap;
    }

    .login-form__field-input {
      width: 80%;
    }
  }

  .submit-button-mobile {
    width: 70%;
    padding: 15px;
    margin: 0 auto 18px;
  }

  .footer-mobile {
    margin: 0;
  }
</style>
